import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import MultiSafepayCheckout from '@shared/components/payments/MultiSafepayCheckout';
import type { CheckoutBranding } from '@shared/utils/checkoutBranding';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding, type ShopTheme } from '../theme';
import { useI18n, type LocaleCode } from '../i18n';
import { formatMoney } from '../i18n/format';
import { AccentButton, ShopFooter, SmallCaps } from '../components/ShopChrome';
import type { ShopLegal } from '../legal';
import HostedPaymentModal from '../components/HostedPaymentModal';
import { collectBrowserInfo } from '@shared/components/payments/browserInfo';
import { giftMspLog } from '../lib/giftMspLog';
import {
  normalizeCompanyDetails,
  type CompanyDetailsErrors,
  type CompanyDetailsField,
  type GiftVoucherBuyerType,
} from '@shared/utils/giftVoucherBuyer';
import BuyerTypeSection from '../components/BuyerTypeSection';
import {
  clearFieldError,
  emptyCompanyDetails,
  explainerPath,
  syncBillingEmail,
  withDefaultCountry,
  type CompanyDetailsForm,
} from '../components/companyDetailsForm';

/** Embedded MultiSafepay component session returned by the purchase endpoint. */
interface GiftComponentSession {
  purchaseId: string;
  apiToken: string;
  env: 'test' | 'live';
  amountCents: number;
  currency: string;
  branding: CheckoutBranding;
  country?: string;
}

/**
 * Payment Component UI locales are a fixed set (`nl_NL`, `en_EN`, …) — not `nl_BE`.
 * Country (BE) is passed separately and is what filters Bancontact vs iDEAL.
 */
function mspLocaleFromShop(locale: LocaleCode): string {
  const lang =
    locale === 'fr-FR' || locale === 'fr-CA' ? 'fr' : locale === 'en' ? 'en' : locale.slice(0, 2);
  if (lang === 'fr') return 'fr_FR';
  if (lang === 'en') return 'en_EN';
  return 'nl_NL';
}

/** Hosted checkout (Mollie) — opened in its own window, never framed. */
interface HostedSession {
  purchaseId: string;
  url: string;
  providerLabel: string;
}

interface CheckoutData {
  itemId: string;
  type: string;
  amount: number;
  currency: string;
  itemName?: string;
  recipientName: string;
  recipientFirstName: string;
  recipientEmail: string;
  personalMessage: string;
  deliveryMethod: 'email' | 'print' | 'myself';
  buyerName: string;
  buyerEmail: string;
  locationId?: string;
}

const POLL_INTERVAL_MS = 2500;

/**
 * The hosted payment already opened for this exact order. Paying again (after closing the
 * window or cancelling the modal) reopens it instead of creating a second purchase that
 * could be paid too, leaving the guest with two vouchers and two charges (F-50).
 */
const PENDING_PURCHASE_KEY = 'giftVoucherPendingPurchase';

interface PendingPurchase extends HostedSession {
  fingerprint: string;
}

function readPendingPurchase(fingerprint: string): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingPurchase;
    return pending && pending.fingerprint === fingerprint && pending.purchaseId && pending.url ? pending : null;
  } catch {
    return null;
  }
}

function writePendingPurchase(pending: PendingPurchase | null): void {
  try {
    if (pending) sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(pending));
    else sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    /* storage unavailable: a retry then creates a new purchase, as before */
  }
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [branding, setBranding] = useState<ShopBranding | null>(null);
  const [venue, setVenue] = useState<{
    name?: string;
    address?: string;
    country?: string;
    legal?: ShopLegal;
  }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [component, setComponent] = useState<GiftComponentSession | null>(null);
  const [hosted, setHosted] = useState<HostedSession | null>(null);
  const [popupClosed, setPopupClosed] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [buyerType, setBuyerType] = useState<GiftVoucherBuyerType>('private');
  const [company, setCompany] = useState<CompanyDetailsForm>(() => emptyCompanyDetails());
  const [companyErrors, setCompanyErrors] = useState<CompanyDetailsErrors>({});
  const [billingEmailEdited, setBillingEmailEdited] = useState(false);
  const popupRef = useRef<Window | null>(null);

  const updateCompany = (field: CompanyDetailsField, value: string) => {
    if (field === 'billing_email') setBillingEmailEdited(true);
    setCompany((prev) => ({ ...prev, [field]: value }));
    // The banner only says "fix the fields below"; the inline messages carry on from here.
    setError((prev) => (prev === t('companyDetails.error_fix') ? null : prev));
    setCompanyErrors((prev) => clearFieldError(prev, field));
  };

  // The purchase confirmation goes to the buyer's own email unless they name another.
  useEffect(() => {
    setCompany((prev) => syncBillingEmail(prev, checkoutData?.buyerEmail, billingEmailEdited));
  }, [checkoutData?.buyerEmail, billingEmailEdited]);

  const theme = useMemo(() => resolveShopTheme(branding), [branding]);

  useEffect(() => {
    const stored = sessionStorage.getItem('checkoutData') || sessionStorage.getItem('giftVoucherCheckout');

    if (!stored) {
      setError(t('checkout.expired_body'));
      setLoading(false);
      return;
    }

    try {
      const raw = JSON.parse(stored);
      const data: CheckoutData = {
        itemId: raw.itemId,
        type: raw.type || raw.itemType,
        amount: raw.amount || raw.itemAmount,
        currency: raw.currency,
        itemName: raw.itemName,
        recipientName: raw.recipientName,
        recipientFirstName: raw.recipientFirstName,
        recipientEmail: raw.recipientEmail,
        personalMessage: raw.personalMessage || raw.message,
        deliveryMethod: raw.deliveryMethod === 'self' ? 'myself' : raw.deliveryMethod || 'email',
        buyerName: raw.buyerName,
        buyerEmail: raw.buyerEmail,
        locationId: raw.locationId,
      };

      if (
        !data.itemId ||
        !data.type ||
        !data.amount ||
        !data.currency ||
        !data.recipientName ||
        !data.recipientEmail ||
        !data.buyerName ||
        !data.buyerEmail
      ) {
        throw new Error('Missing required checkout data fields');
      }
      if (data.amount <= 0) throw new Error('Invalid amount');

      setCheckoutData(data);
    } catch (err) {
      console.error('Error parsing checkout data:', err);
      setError(err instanceof Error ? err.message : t('checkout.expired_body'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // The venue's branding paints this page too — it is still their shop.
  useEffect(() => {
    const locationId = checkoutData?.locationId;
    if (!locationId) return;
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${getApiBaseUrl()}/gift-vouchers/shop/${locationId}`);
        if (!r.ok || !active) return;
        const data = await r.json();
        setBranding(data.branding || null);
        setVenue({
          name: data.location_name,
          address: data.location_address,
          country: typeof data.location_country === 'string' ? data.location_country : undefined,
          legal: data.legal || undefined,
        });
        applyVenueLocale(data.location_language);
        if (typeof data.location_country === 'string') {
          const country = data.location_country;
          setCompany((prev) => withDefaultCountry(prev, country));
        }
      } catch {
        /* branding is a nicety — the checkout works without it */
      }
    })();
    return () => {
      active = false;
    };
  }, [checkoutData?.locationId, applyVenueLocale]);

  const purchaseId = component?.purchaseId || hosted?.purchaseId || null;
  const waitingForPayment = (submitted && !!component) || !!hosted;

  // Both flows finish the same way: the webhook marks the purchase paid, we notice and move on.
  useEffect(() => {
    if (!waitingForPayment || !purchaseId) return;
    const apiBaseUrl = getApiBaseUrl();
    let active = true;

    const poll = async () => {
      try {
        const r = await fetch(`${apiBaseUrl}/gift-vouchers/purchase/${purchaseId}/status`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        giftMspLog('checkout.poll', { purchase_id: purchaseId, status: d.status || null });
        // needs_review: paid, but staff must issue the voucher — the success page explains.
        if (d.status === 'paid' || d.status === 'completed' || d.status === 'needs_review') {
          writePendingPurchase(null);
          active = false;
          try {
            popupRef.current?.close();
          } catch {
            /* the window may already be gone */
          }
          navigate(`/success/${purchaseId}`);
        }
      } catch {
        /* transient network error — keep polling */
      }
    };

    void poll();
    const id = window.setInterval(() => {
      if (active) void poll();
    }, POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [waitingForPayment, purchaseId, navigate]);

  // Notice when the guest closes the hosted window so we can offer a way back in.
  useEffect(() => {
    if (!hosted) return;
    const id = window.setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        setPopupClosed(true);
      }
    }, 800);
    return () => window.clearInterval(id);
  }, [hosted]);

  const openHostedWindow = useCallback((url: string): Window | null => {
    const width = 600;
    const height = 800;
    const left = Math.max(0, (window.screen.width - width) / 2);
    const top = Math.max(0, (window.screen.height - height) / 2);
    try {
      return window.open(
        url,
        'gift-voucher-payment',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    } catch {
      return null;
    }
  }, []);

  const handlePayment = async () => {
    if (!checkoutData) {
      setError(t('checkout.error_missing_data'));
      return;
    }

    // Company details are checked before the payment window opens: once it is open the
    // guest is on the provider's page and cannot come back to fix a typo.
    let companyDetails: ReturnType<typeof normalizeCompanyDetails> | null = null;
    if (buyerType === 'company') {
      companyDetails = normalizeCompanyDetails(company);
      if (!companyDetails.ok) {
        setCompanyErrors(companyDetails.errors);
        setError(t('companyDetails.error_fix'));
        return;
      }
    }

    setProcessing(true);
    setError(null);
    giftMspLog('checkout.pay_click', {
      location_id: checkoutData.locationId || null,
      amount: checkoutData.amount,
      currency: checkoutData.currency,
    });

    // Opened on the click itself: a window created later, after the API answers, is a popup
    // a blocker will swallow. If the venue turns out to be on MultiSafepay we close it again.
    const placeholder = openHostedWindow('about:blank');
    popupRef.current = placeholder;

    const fingerprint = JSON.stringify({
      checkoutData,
      buyerType,
      company: companyDetails?.ok ? companyDetails.company : null,
    });

    const showHosted = (session: HostedSession) => {
      if (placeholder && !placeholder.closed) {
        placeholder.location.href = session.url;
        try {
          placeholder.focus();
        } catch {
          /* focus is best effort */
        }
        setPopupBlocked(false);
      } else {
        setPopupBlocked(true);
      }
      setPopupClosed(false);
      setHosted(session);
      setProcessing(false);
    };

    try {
      const apiBaseUrl = getApiBaseUrl();
      const locationId = checkoutData.locationId || window.location.pathname.split('/')[2];

      if (!locationId) {
        throw new Error(t('checkout.error_no_location'));
      }

      // Same order already has an open payment: go back to it rather than start another.
      const pending = readPendingPurchase(fingerprint);
      if (pending) {
        let pendingStatus: string | null = null;
        try {
          const r = await fetch(`${apiBaseUrl}/gift-vouchers/purchase/${pending.purchaseId}/status`, {
            cache: 'no-store',
          });
          if (r.ok) pendingStatus = (await r.json()).status || null;
        } catch {
          pendingStatus = null;
        }
        giftMspLog('checkout.pending_purchase', { purchase_id: pending.purchaseId, status: pendingStatus });
        if (pendingStatus === 'pending') {
          showHosted({ purchaseId: pending.purchaseId, url: pending.url, providerLabel: pending.providerLabel });
          return;
        }
        if (pendingStatus === 'paid' || pendingStatus === 'needs_review') {
          try {
            placeholder?.close();
          } catch {
            /* nothing to close */
          }
          writePendingPurchase(null);
          navigate(`/success/${pending.purchaseId}`);
          return;
        }
        // Failed, cancelled or expired (or unknown): that payment cannot be finished, start a new one.
        writePendingPurchase(null);
      }

      const purchaseResponse = await fetch(`${apiBaseUrl}/gift-vouchers/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_id: locationId,
          product_id:
            checkoutData.type === 'product' && checkoutData.itemId !== 'custom' ? checkoutData.itemId : undefined,
          package_id: checkoutData.type === 'package' ? checkoutData.itemId : undefined,
          custom_amount: checkoutData.itemId === 'custom' || checkoutData.type === 'custom',
          amount: checkoutData.amount,
          currency: checkoutData.currency,
          buyer_name: checkoutData.buyerName,
          buyer_email: checkoutData.buyerEmail,
          buyer_first_name: checkoutData.buyerName?.split(' ')[0] || checkoutData.buyerName,
          recipient_name: checkoutData.recipientName,
          recipient_email: checkoutData.recipientEmail,
          recipient_first_name:
            checkoutData.recipientFirstName ||
            checkoutData.recipientName?.split(' ')[0] ||
            checkoutData.recipientName,
          message: checkoutData.personalMessage || undefined,
          buyer_type: companyDetails?.ok ? 'company' : 'private',
          company: companyDetails?.ok ? companyDetails.company : undefined,
          // The purchase confirmation is written in the language the guest bought in.
          locale,
          redirect_url: `${window.location.origin}/success/{paymentId}`,
          webhook_url: `${apiBaseUrl}/gift-vouchers/webhook/mollie`,
          // MultiSafepay's own hosted checkout, in its own window, rather than the embedded
          // drop-in. MSP renders the card form, collects the 3-D Secure fingerprint and paints
          // the Bancontact/iDEAL QR itself, so none of the component's order/QR/redirect
          // handshake is ours to get right. Send 'component' here to go back to the drop-in.
          mode: 'redirect',
          // Shop-page fingerprint for Cloud Logging only. Hosted MSP collects 3DS itself.
          browser: collectBrowserInfo(),
        }),
      });

      if (!purchaseResponse.ok) {
        let errorMessage = t('checkout.error_generic');
        try {
          const errorData = await purchaseResponse.json();
          if (errorData.error?.code === 'invalid_company') {
            setCompanyErrors(errorData.error.fields || {});
            errorMessage = t('companyDetails.error_fix');
          } else {
            errorMessage = errorData.error?.message || errorMessage;
          }
        } catch {
          errorMessage = `Server error: ${purchaseResponse.status} ${purchaseResponse.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await purchaseResponse.json();
      giftMspLog('checkout.purchase.response', {
        http_status: purchaseResponse.status,
        mode: result.mode || null,
        purchase_id: result.purchase_id || null,
        env: result.env || null,
        country: result.country || null,
        has_api_token: Boolean(result.apiToken),
        has_payment_url: Boolean(result.payment_url || result.checkout_url),
      });

      // MultiSafepay: the payment form is part of the shop, rendered right here.
      if (result.mode === 'component' && result.apiToken) {
        try {
          placeholder?.close();
        } catch {
          /* nothing to close */
        }
        popupRef.current = null;
        setComponent({
          purchaseId: result.purchase_id,
          apiToken: result.apiToken,
          env: result.env || 'test',
          amountCents: result.amountCents ?? Math.round(checkoutData.amount * 100),
          currency: result.currency || checkoutData.currency,
          branding: result.branding as CheckoutBranding,
          country: typeof result.country === 'string' ? result.country : undefined,
        });
        setProcessing(false);
        return;
      }

      // Mollie (and any other hosted provider): their page, in its own window.
      const paymentUrl = result.payment_url || result.checkout_url;
      if (!paymentUrl) {
        throw new Error(t('checkout.error_no_url'));
      }
      new URL(paymentUrl);

      // The hosted window is now the normal path, so name the provider the guest is about to
      // land on rather than falling back to "our payment provider".
      const PROVIDER_NAMES: Record<string, string> = {
        mollie: 'Mollie',
        multisafepay: 'MultiSafepay',
      };
      const providerLabel =
        PROVIDER_NAMES[String(result.payment_provider)] || t('hosted.provider_fallback');

      const session: HostedSession = { purchaseId: result.purchase_id, url: paymentUrl, providerLabel };
      writePendingPurchase({ ...session, fingerprint });
      showHosted(session);
    } catch (err) {
      giftMspLog('checkout.purchase.exception', {
        error: err instanceof Error ? err.message : String(err),
      });
      console.error('Payment error:', err);
      try {
        placeholder?.close();
      } catch {
        /* nothing to close */
      }
      popupRef.current = null;
      setError(err instanceof Error ? err.message : t('checkout.error_generic'));
      setProcessing(false);
    }
  };

  const reopenHosted = () => {
    if (!hosted) return;
    const win = openHostedWindow(hosted.url);
    popupRef.current = win;
    if (win) {
      setPopupBlocked(false);
      setPopupClosed(false);
      try {
        win.focus();
      } catch {
        /* focus is best effort */
      }
    } else {
      setPopupBlocked(true);
    }
  };

  const cancelHosted = () => {
    try {
      popupRef.current?.close();
    } catch {
      /* the window may already be gone */
    }
    popupRef.current = null;
    setHosted(null);
    setPopupClosed(false);
    setPopupBlocked(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={themeCssVars(theme)}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
      </div>
    );
  }

  if (!checkoutData) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5" style={themeCssVars(theme)}>
        <div className="max-w-md text-center">
          <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>
            {t('checkout.expired_title')}
          </h1>
          <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: '0 0 24px' }}>
            {error || t('checkout.expired_body')}
          </p>
          <AccentButton theme={theme} onClick={() => navigate('/')}>
            {t('checkout.start_over')}
          </AccentButton>
        </div>
      </div>
    );
  }

  const total = formatMoney(checkoutData.amount, checkoutData.currency, locale);

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <header
        className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10 sm:py-[22px]"
        style={{ borderBottom: `1px solid ${theme.hairline}` }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2"
          style={{ font: `500 14px/1 ${theme.fontFamily}`, color: theme.body }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <SmallCaps theme={theme}>{t('checkout.step')}</SmallCaps>
      </header>

      <main className="mx-auto grid w-full max-w-[1100px] flex-1 gap-10 px-5 py-10 sm:px-10 sm:py-14 lg:grid-cols-[1.35fr_.65fr] lg:gap-12">
        <div>
          <div className="mb-8 flex gap-2">
            <div className="h-0.5 flex-1" style={{ background: theme.ink }} />
            <div className="h-0.5 flex-1" style={{ background: theme.ink }} />
          </div>

          <h1
            style={{
              margin: '0 0 8px',
              font: `500 clamp(26px, 4.5vw, 34px)/1.15 ${theme.fontFamily}`,
              letterSpacing: '-.02em',
              color: theme.ink,
            }}
          >
            {t('checkout.title')}
          </h1>
          <p style={{ margin: '0 0 28px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
            {t('checkout.subtitle')}
          </p>

          <div
            className="p-6"
            style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
          >
            {error && !component && (
              <p className="mb-5" style={{ font: `400 14px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
                {error}
              </p>
            )}

            {component ? (
              <MultiSafepayCheckout
                env={component.env}
                apiToken={component.apiToken}
                amountCents={component.amountCents}
                currency={component.currency}
                country={component.country || venue.country || 'BE'}
                locale={mspLocaleFromShop(locale)}
                // gift.10seat.com is registered with MultiSafepay as an Apple Pay merchant
                // domain, so the drop-in may show the wallet row here. Safari/iOS only.
                wallets
                branding={component.branding}
                title={component.branding.name || venue.name || 'Gift voucher'}
                createOrder={async (paymentData, browser) => {
                  giftMspLog('checkout.pay.start', {
                    purchase_id: component.purchaseId,
                    gateway: paymentData?.gateway || null,
                    has_browser: Boolean(browser),
                    payload_len: paymentData?.payload ? String(paymentData.payload).length : 0,
                  });
                  const r = await fetch(
                    `${getApiBaseUrl()}/gift-vouchers/purchase/${component.purchaseId}/pay`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      // `browser` is the 3-D Secure fingerprint — without it MultiSafepay
                      // cannot start 3DS and declines Bancontact outright.
                      body: JSON.stringify({ payment_data: paymentData, browser }),
                    }
                  );
                  const body = await r.json();
                  giftMspLog('checkout.pay.response', {
                    purchase_id: component.purchaseId,
                    http_status: r.status,
                    success: body?.success ?? false,
                    error: body?.error || null,
                    // What MultiSafepay actually said. This is the field to read when the
                    // checkout refuses every method — it names the reason without a log query.
                    provider_error_code: body?.provider_error_code ?? null,
                    provider_error: body?.provider_error || null,
                    order_id: body?.order?.order_id || null,
                    order_status: body?.order?.status || null,
                    has_payment_url: Boolean(body?.order?.payment_url),
                  });
                  if (!r.ok || !body?.success) {
                    // The provider's own wording is for the logs, never for the guest: it is
                    // written for integrators and may name gateways the shop does not offer.
                    return { success: false, error: t('checkout.error_generic') };
                  }
                  return body;
                }}
                onSubmitted={(order) => {
                  giftMspLog('checkout.submitted', {
                    purchase_id: component.purchaseId,
                    order_id: order?.order_id || null,
                    status: order?.status || null,
                  });
                  setSubmitted(true);
                }}
                onError={(msg) => {
                  giftMspLog('checkout.component_error', {
                    purchase_id: component.purchaseId,
                    message: msg,
                  });
                  setError(msg);
                }}
                onClientLog={(event, fields) =>
                  giftMspLog(event, { purchase_id: component.purchaseId, ...fields })
                }
                labels={{
                  genericError: t('checkout.error_generic'),
                  loadingBody: t('checkout.provider_loading'),
                  verifyingBody: t('checkout.provider_verifying'),
                  loadFailedBody: t('checkout.provider_failed'),
                  totalLabel: t('order.total'),
                }}
              />
            ) : (
              <>
                <BuyerTypeSection
                  theme={theme}
                  buyerType={buyerType}
                  onBuyerTypeChange={(next) => {
                    setBuyerType(next);
                    if (next === 'private') {
                      setCompanyErrors({});
                      setError((prev) => (prev === t('companyDetails.error_fix') ? null : prev));
                    }
                  }}
                  company={company}
                  onCompanyChange={updateCompany}
                  errors={companyErrors}
                  venueCountry={venue.country}
                  explainerHref={explainerPath(locale, venue.country)}
                  disabled={processing || !!hosted}
                />
                <div className="flex items-baseline justify-between pb-5">
                  <span style={{ font: `500 15px/1 ${theme.fontFamily}`, color: theme.body }}>{t('order.total')}</span>
                  <span
                    style={{
                      font: `500 28px/1 ${theme.fontFamily}`,
                      color: theme.ink,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {total}
                  </span>
                </div>
                <div className="h-px" style={{ background: theme.hairlineSoft }} />
                <AccentButton
                  theme={theme}
                  onClick={handlePayment}
                  disabled={processing || !!hosted}
                  className="mt-5 w-full"
                  style={{ padding: '16px', font: `600 16px/1 ${theme.fontFamily}` }}
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('checkout.opening')}
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      {t('checkout.pay', { amount: total })}
                    </>
                  )}
                </AccentButton>
                <p
                  className="mt-4 text-center"
                  style={{ font: `400 12px/1.55 ${theme.fontFamily}`, color: theme.label, margin: '16px 0 0' }}
                >
                  {t('checkout.terms')}
                </p>
              </>
            )}
          </div>
        </div>

        <aside
          className="h-fit p-6 lg:sticky lg:top-8"
          style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
        >
          <SmallCaps theme={theme}>{t('order.heading')}</SmallCaps>
          <div className="mt-5 flex flex-col gap-3">
            <SummaryRow theme={theme} label={checkoutData.itemName || t('order.gift_voucher')} value={total} />
            <SummaryRow
              theme={theme}
              label={t('order.for')}
              value={checkoutData.recipientFirstName || checkoutData.recipientName}
            />
            <SummaryRow
              theme={theme}
              label={t('order.delivery')}
              value={t(
                `order.delivery_${checkoutData.deliveryMethod === 'myself' ? 'self' : checkoutData.deliveryMethod}`
              )}
            />
            <SummaryRow
              theme={theme}
              label={t('buyer.buying_as')}
              value={
                buyerType === 'company' ? company.legal_name.trim() || t('buyer.company') : t('buyer.private')
              }
            />
            <div className="h-px" style={{ background: theme.hairlineSoft }} />
            <div className="flex items-baseline justify-between">
              <span style={{ font: `500 15px/1 ${theme.fontFamily}`, color: theme.ink }}>{t('order.total')}</span>
              <span
                style={{
                  font: `500 26px/1 ${theme.fontFamily}`,
                  color: theme.ink,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {total}
              </span>
            </div>
          </div>
          {checkoutData.personalMessage && (
            <p
              className="mt-5 pl-3"
              style={{
                borderLeft: `2px solid ${theme.hairlineSoft}`,
                font: `400 13px/1.55 ${theme.fontFamily}`,
                color: theme.muted,
                margin: '20px 0 0',
              }}
            >
              {checkoutData.personalMessage}
            </p>
          )}
          <p
            className="mt-6 pt-5"
            style={{
              borderTop: `1px solid ${theme.hairlineSoft}`,
              font: `400 13px/1.6 ${theme.fontFamily}`,
              color: theme.label,
              margin: 0,
            }}
          >
            {t('order.note_refund')}
          </p>
        </aside>
      </main>

      <ShopFooter
        theme={theme}
        venueName={venue.name}
        venueAddress={venue.address}
        legal={venue.legal}
        locationId={checkoutData?.locationId}
      />

      {hosted && (
        <HostedPaymentModal
          theme={theme}
          providerLabel={hosted.providerLabel}
          amountLabel={total}
          popupClosed={popupClosed}
          popupBlocked={popupBlocked}
          onReopen={reopenHosted}
          onOpenHere={() => window.location.assign(hosted.url)}
          onCancel={cancelHosted}
        />
      )}
    </div>
  );
};

const SummaryRow: React.FC<{ theme: ShopTheme; label: string; value: string }> = ({ theme, label, value }) => (
  <div className="flex justify-between gap-4" style={{ font: `400 14px/1.4 ${theme.fontFamily}`, color: theme.body }}>
    <span className="min-w-0">{label}</span>
    <span className="min-w-0 truncate" style={{ color: theme.ink, whiteSpace: 'nowrap' }} title={value}>
      {value}
    </span>
  </div>
);

export default CheckoutPage;
